import * as React from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { useToast } from "src/hooks/use-toast";

import { useLlmConversationActionsDeleteConversationMutation } from "./__generated__/useLlmConversationActionsDeleteConversationMutation.graphql";
import { useLlmConversationActionsRenameConversationMutation } from "./__generated__/useLlmConversationActionsRenameConversationMutation.graphql";

const deleteConversationMutation = graphql`
  mutation useLlmConversationActionsDeleteConversationMutation($input: DeleteConversationInput!) {
    deleteConversation(input: $input) {
      success
      deletedConversationId
    }
  }
`;

const renameConversationMutation = graphql`
  mutation useLlmConversationActionsRenameConversationMutation($input: UpdateLlmConversationInput!) {
    updateLlmConversation(input: $input) {
      success
      error
      conversation {
        id
        title
      }
    }
  }
`;

interface UseLlmConversationManagementOptions {
  refetchConversations: () => void;
}

export const useLlmConversationManagement = ({
  refetchConversations,
}: UseLlmConversationManagementOptions) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [commitDeleteConversation, isDeleteInFlight] =
    useMutation<useLlmConversationActionsDeleteConversationMutation>(deleteConversationMutation);
  const [commitRenameConversation] = useMutation<useLlmConversationActionsRenameConversationMutation>(
    renameConversationMutation,
  );

  const [deletingConversationId, setDeletingConversationId] = React.useState<string | null>(null);
  const [deletingConversationTitle, setDeletingConversationTitle] = React.useState<string>("");

  const handleDeleteConversation = (
    conversationId: string,
    title: string,
    event: React.MouseEvent,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setDeletingConversationId(conversationId);
    setDeletingConversationTitle(title);
    return { conversationId, title };
  };

  const confirmDeleteConversation = (conversationId: string | null) => {
    if (!conversationId) {
      return;
    }

    commitDeleteConversation({
      variables: {
        input: {
          llmConversationId: conversationId,
        },
      },
      onCompleted: (response) => {
        if (response.deleteConversation.success) {
          refetchConversations();

          if (window.location.pathname.includes(`/chat/c/${conversationId}`)) {
            navigate("/chat");
          }
        }

        setDeletingConversationId(null);
        setDeletingConversationTitle("");
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: "Failed to delete conversation: " + error.message,
          variant: "error",
        });
      },
    });
  };

  const handleRenameConversation = (conversationId: string, newTitle: string) => {
    const normalizedTitle = newTitle.trim();
    const maxTitleLength = 255;

    if (!normalizedTitle) {
      toast({
        title: "Error",
        description: "Title cannot be empty",
        variant: "error",
      });
      return;
    }

    if (normalizedTitle.length > maxTitleLength) {
      toast({
        title: "Error",
        description: `Title must be ${maxTitleLength} characters or less`,
        variant: "error",
      });
      return;
    }

    commitRenameConversation({
      variables: {
        input: {
          conversationId,
          title: normalizedTitle,
        },
      },
      onCompleted: (response) => {
        if (response.updateLlmConversation.success) {
          refetchConversations();
        } else {
          toast({
            title: "Error",
            description: response.updateLlmConversation.error || "Failed to rename conversation",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: "Failed to rename conversation: " + error.message,
          variant: "error",
        });
      },
    });
  };

  return {
    handleDeleteConversation,
    confirmDeleteConversation,
    handleRenameConversation,
    isDeleting: isDeleteInFlight,
    deletingConversationId,
    deletingConversationTitle,
    setDeletingConversationId,
    setDeletingConversationTitle,
  };
};
